import React, { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useLocation } from "react-router-dom";
import { PublicPage, Eyebrow } from "./public/PublicLayout";

export default function FinishSignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showForm, setShowForm] = useState(true);
  const [inviteToken, setInviteToken] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("token");
    if (tokenFromUrl) {
      setInviteToken(tokenFromUrl);
    } else {
      setMessage({ text: "This invite link is missing or invalid.", type: "error" });
      setShowForm(false);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setMessage({ text: "", type: "" });

    // Basic validation
    if (!inviteToken) {
      setMessage({ text: "This invite link is missing its token.", type: "error" });
      setProcessing(false);
      return;
    }
    if (!name.trim() || !email.trim()) {
      setMessage({ text: "Enter your name and email.", type: "error" });
      setProcessing(false);
      return;
    }
    if (password !== confirmPassword) {
      setMessage({ text: "Passwords don't match.", type: "error" });
      setProcessing(false);
      return;
    }
    if (password.length < 6) {
      setMessage({ text: "Password must be at least 6 characters.", type: "error" });
      setProcessing(false);
      return;
    }

    // Logging all values with types for debugging
    console.log("FinishSignUp.js: About to submit payload:");
    console.log("token:", inviteToken, typeof inviteToken, inviteToken.length, inviteToken);
    console.log("name:", name, typeof name, name.length, name);
    console.log("email:", email, typeof email, email.length, email);
    console.log("password:", password, typeof password, password.length, password);

    // Type & empty checks
    if (
      typeof inviteToken !== "string" ||
      typeof name !== "string" ||
      typeof email !== "string" ||
      typeof password !== "string" ||
      !inviteToken.trim() ||
      !name.trim() ||
      !email.trim() ||
      !password
    ) {
      console.warn("[FinishSignUp.js] Submission stopped: One or more fields are not strings or are empty.");
      setMessage({ text: "Some details are missing or invalid.", type: "error" });
      setProcessing(false);
      return;
    }

    try {
      const functions = getFunctions();
      const createNewUser = httpsCallable(functions, "createNewUserFromInvite");
      const payload = {
        token: inviteToken.trim(),
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password: password,
      };

      // Log the final payload (not just variables)
      console.log("Payload being sent:", payload);

      const result = await createNewUser(payload);

      if (result.data && result.data.success) {
        setMessage({
          text: result.data.message || "Your account is set up. You can now sign in.",
          type: "success",
        });
        setShowForm(false);
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setMessage({
          text: result.data?.message || "Registration failed. Try again.",
          type: "error",
        });
      }
    } catch (error) {
      let friendlyMessage = "Registration failed: ";
      if (error.code === "already-exists") {
        friendlyMessage += "This email address is already registered.";
      } else if (error.code === "not-found") {
        friendlyMessage += "This invite link is invalid or has expired.";
        setShowForm(false);
      } else if (error.code === "invalid-argument") {
        friendlyMessage += "Some details are missing. Fill in every field.";
      } else {
        friendlyMessage += error.message;
      }
      setMessage({ text: friendlyMessage, type: "error" });
      console.error("FinishSignUp.js: --- ERROR calling createNewUserFromInvite function ---", error);
    }
    setProcessing(false);
  };

  return (
    <PublicPage>
      <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-md mx-auto">
          <Eyebrow>Invitation</Eyebrow>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
            Finish setting up
            <span className="block pub-accent">your account</span>
          </h1>

          <div className="pub-surface pub-line border rounded-2xl shadow-2xl shadow-black/30 p-8 sm:p-10 mt-8">
            {showForm ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="pub-text block font-semibold mb-2">
                    Full name
                  </label>
                  <div className="relative">
                    <i className="fas fa-user pub-faint absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      id="name"
                      className="pub-input w-full py-3 pl-12 pr-4 rounded-lg transition"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="pub-text block font-semibold mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <i className="fas fa-envelope pub-faint absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      id="email"
                      className="pub-input w-full py-3 pl-12 pr-4 rounded-lg transition"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="The email your invite was sent to"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="password" className="pub-text block font-semibold mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <i className="fas fa-lock pub-faint absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      id="password"
                      className="pub-input w-full py-3 pl-12 pr-4 rounded-lg transition"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="At least 6 characters"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="pub-text block font-semibold mb-2">
                    Confirm password
                  </label>
                  <div className="relative">
                    <i className="fas fa-lock pub-faint absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      id="confirmPassword"
                      className="pub-input w-full py-3 pl-12 pr-4 rounded-lg transition"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Re-enter password"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-3 py-3.5 rounded-lg pub-btn text-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={processing || !inviteToken}
                >
                  {processing
                    ? <><i className="fas fa-spinner fa-spin" />Setting up…</>
                    : <>Set password and finish <i className="fas fa-arrow-right" /></>}
                </button>
              </form>
            ) : (
              message.text && (
                <div>
                  <div
                    role={message.type === "success" ? "status" : "alert"}
                    className={`px-4 py-3 rounded-lg ${
                      message.type === "success" ? "pub-success" : "pub-error"
                    }`}
                  >
                    <p className="font-bold">
                      {message.type === "success" ? "Done." : "Something went wrong."}
                    </p>
                    <p className="mt-1">{message.text}</p>
                  </div>
                  {message.type === "success" && (
                    <button
                      onClick={() => navigate("/login")}
                      className="mt-6 w-full flex items-center justify-center gap-3 py-3.5 rounded-lg pub-btn font-semibold transition"
                    >
                      Go to sign in <i className="fas fa-arrow-right" />
                    </button>
                  )}
                  {message.type === "error" && !showForm && (
                    <button
                      onClick={() => navigate("/login")}
                      className="pub-line pub-text mt-6 w-full py-3.5 rounded-lg border font-semibold hover:opacity-80 transition"
                    >
                      Go to sign in or request a new invite
                    </button>
                  )}
                </div>
              )
            )}
            {message.text && showForm && (
              <div
                role={message.type === "success" ? "status" : "alert"}
                className={`mt-5 px-4 py-3 rounded-lg text-sm font-semibold ${
                  message.type === "success" ? "pub-success" : "pub-error"
                }`}
              >
                {message.text}
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
